import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/thoratech/Nav";
import Footer from "@/components/thoratech/Footer";
import Seo from "@/seo/Seo";
import MarkdownBody from "@/components/thoratech/MarkdownBody";
import { getPost, BLOG_POSTS } from "@/content/blog";
import { BRAND } from "@/content/thoratech";
import { ArrowLeft, ArrowUpRight, Clock, Share2 } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch (e) {
      /* user cancelled or blocked */
    }
  };

  return (
    <>
      <Nav />
      <Seo route="/blog/:slug" slug={slug} />
      <main data-testid="blog-post-page" className="bg-white pt-[110px] md:pt-[140px] pb-24">
        <article>
          {/* Hero */}
          <header className="max-w-4xl mx-auto px-6 md:px-12 mb-12">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="flex w-fit items-center gap-2 text-sm font-medium text-[#01080E]/70 hover:text-[#2AC0A5] transition-colors mb-10"
            >
              <ArrowLeft size={16} /> All articles
            </Link>
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="block h-px w-8 bg-[#2AC0A5]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#2AC0A5]">
                {post.category}
              </span>
            </div>
            <h1
              data-testid="blog-post-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-[#01080E]"
            >
              {post.title}
            </h1>
            <p className="mt-6 text-xl text-[#01080E]/70 leading-relaxed">{post.summary}</p>
            <div className="mt-8 flex items-center justify-between border-y border-[#01080E]/10 py-4">
              <span className="font-mono-tech text-xs text-[#01080E]/60 flex items-center gap-2">
                <Clock size={14} /> {post.readMinutes} min read · {post.date}
              </span>
              <button
                onClick={onShare}
                data-testid="blog-share-button"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#01080E] hover:text-[#2AC0A5] transition-colors"
              >
                <Share2 size={14} /> Share
              </button>
            </div>
          </header>

          {/* Hero image */}
          <div className="max-w-5xl mx-auto px-6 md:px-12 mb-14">
            <div className="bg-[#01080E] flex items-center justify-center">
              <img
                src={post.hero}
                alt={post.heroAlt || post.title}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Body */}
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <MarkdownBody body={post.body} />

            {/* CTA */}
            <div className="mt-16 bg-[#01080E] text-white p-8 md:p-10 relative overflow-hidden">
              <div aria-hidden className="absolute inset-0 grid-texture-dark opacity-30" />
              <div className="relative">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#7BDCB5]">
                  From process to progress
                </span>
                {post.cta ? (
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white leading-snug">
                    {post.cta.heading}
                  </h3>
                ) : (
                  <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold text-white leading-snug">
                    Want this run inside your business?{" "}
                    <span className="text-[#2AC0A5]">Start with a free process audit.</span>
                  </h3>
                )}
                <p className="mt-4 text-white/70 leading-relaxed max-w-xl">
                  {post.cta
                    ? post.cta.copy
                    : "We will map your current customer operations, surface the gaps and define the outcomes we can own under contractual SLAs."}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={post.cta ? post.cta.to : "/#contact"}
                    data-testid="blog-cta-audit"
                    className="inline-flex items-center gap-2 bg-[#2AC0A5] text-[#01080E] font-semibold px-6 py-3.5 hover:bg-white transition-colors"
                  >
                    {post.cta ? post.cta.button : "Book a free audit"}
                    <ArrowUpRight size={18} />
                  </Link>
                  <a
                    href={`mailto:${BRAND.contact.email}`}
                    data-testid="blog-cta-email"
                    className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-6 py-3.5 hover:border-[#2AC0A5] hover:text-[#2AC0A5] transition-colors"
                  >
                    {BRAND.contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 border-t border-[#01080E]/10 pt-16">
              <div className="inline-flex items-center gap-3 mb-8">
                <span className="block h-px w-8 bg-[#2AC0A5]" />
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#01080E]">
                  Keep reading
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#01080E]/10 border border-[#01080E]/10">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-related-${p.slug}`}
                    className="group bg-white flex flex-col hover:bg-[#FAFAF8] transition-colors"
                  >
                    <div className="bg-[#01080E] flex items-center justify-center">
                      <img src={p.hero} alt={p.heroAlt || p.title} className="w-full h-auto max-h-[260px] object-contain" loading="lazy" />
                    </div>
                    <div className="p-7">
                      <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#2AC0A5]">
                        {p.category}
                      </span>
                      <h3 className="mt-3 font-display text-xl font-semibold text-[#01080E] group-hover:text-[#2AC0A5] transition-colors leading-tight">
                        {p.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}
